/**
 * CommunityContext — global state for the community (WhatsApp-group) feature.
 *
 * Each "community" in the UI = one backend Community + its default CommunityGroup.
 * All real-time events arrive on the shared chatService socket (community:message:new etc.)
 * because the backend auto-joins cg:{group_id} rooms when client:identify is emitted.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '../services/chatService';
import { voiceFilenameForBlob, voiceMimeForBlob } from '../hooks/useVoiceRecorder';
import { extractPdfPreview } from '../utils/pdfPreview';
import { getDocumentKind } from '../utils/documentFileTypes';
import {
  Community,
  CommunityGroup,
  CommunityMessage,
  CommunityMember,
  CommunityInvite,
  listMyCommunities,
  discoverPublicCommunities,
  joinPublicCommunity as apiJoinPublicCommunity,
  getCommunity,
  redeemInvite,
  createCommunity,
  createGroup,
  listGroups,
  listMessages,
  sendMessage as apiSendMessage,
  uploadAndSendMediaMessage,
  uploadAndSendDocumentMessage,
  sendLocationMessage as apiSendLocationMessage,
  inferMediaMessageType,
  validateMediaFile,
  type CommunityMediaMessageType,
  deleteMessage as apiDeleteMessage,
  pinMessage as apiPinMessage,
  forwardMessages as apiForwardMessages,
  markRead,
  leaveCommunity as apiLeaveCommunity,
  transferOwnership as apiTransferOwnership,
  deleteCommunity as apiDeleteCommunity,
  listCommunityMembers,
  updateCommunity as apiUpdateCommunity,
  uploadCommunityAvatar as apiUploadCommunityAvatar,
  removeCommunityMember as apiRemoveCommunityMember,
  setCommunityMemberRole,
  listCommunityInvites,
  deleteCommunityInvite,
  createInvite,
  joinGroupRoom,
  leaveGroupRoom,
  emitTyping,
  emitStopTyping,
} from '../services/communityService';
import { isInviteConsumed, isInviteListed } from '../utils/communityPermissions';
import { getCommunitySendFailureMessage } from '../utils/communityApiErrors';

const ensureActiveInviteInflight = new Map<string, Promise<CommunityInvite[]>>();

async function fetchListedInvites(communityId: string): Promise<CommunityInvite[]> {
  const invites = await listCommunityInvites(communityId, { noCache: true });
  return invites.filter(isInviteListed);
}

async function reloadInvitesAfterDelete(communityId: string): Promise<CommunityInvite[]> {
  return fetchListedInvites(communityId);
}

async function loadInvitesWithActiveClientInvite(communityId: string): Promise<CommunityInvite[]> {
  const inflight = ensureActiveInviteInflight.get(communityId);
  if (inflight) return inflight;

  const promise = (async () => {
    const invites = await fetchListedInvites(communityId);
    if (invites.some((i) => !isInviteConsumed(i))) return invites;
    const invite = await createInvite(communityId);
    return [invite, ...invites];
  })().finally(() => {
    ensureActiveInviteInflight.delete(communityId);
  });

  ensureActiveInviteInflight.set(communityId, promise);
  return promise;
}

async function resolveDefaultGroup(communityId: string): Promise<CommunityGroup | null> {
  const retryDelaysMs = [0, 400, 900, 1500];

  const pickDefault = (groups: CommunityGroup[]): CommunityGroup | null =>
    groups.find((g) => g.is_default && g.is_active) ??
    groups.find((g) => g.is_active) ??
    null;

  for (const delay of retryDelaysMs) {
    if (delay > 0) {
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    const mineGroups = await listGroups(communityId, 'mine', { noCache: true });
    const fromMine = pickDefault(mineGroups);
    if (fromMine) return fromMine;

    const publicGroups = await listGroups(communityId, 'public', { noCache: true });
    const fromPublic =
      pickDefault(publicGroups.filter((g) => g.my_membership)) ??
      publicGroups.find((g) => g.is_active && g.my_membership) ??
      null;
    if (fromPublic) return fromPublic;
  }

  return null;
}

function upsertCommunityInList(list: Community[], community: Community): Community[] {
  const idx = list.findIndex((c) => c.community_id === community.community_id);
  if (idx === -1) return [community, ...list];
  return list.map((c) => (c.community_id === community.community_id ? { ...c, ...community } : c));
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OptimisticMessage extends CommunityMessage {
  _optimistic?: boolean;
  _failed?: boolean;
  /** User-facing reason when `_failed` (e.g. content blocked). */
  _error?: string;
}

interface CommunityContextType {
  // List
  communities: Community[];
  loadingCommunities: boolean;
  communitiesLoadError: string | null;
  discoverCommunities: Community[];
  loadingDiscover: boolean;
  discoverTotal: number;
  joiningCommunityId: string | null;
  // Selected community state
  selectedCommunity: Community | null;
  activeGroup: CommunityGroup | null;
  // Messages
  messages: OptimisticMessage[];
  loadingMessages: boolean;
  communityOpenError: string | null;
  failedCommunityId: string | null;
  hasMoreMessages: boolean;
  pinnedMessage: CommunityMessage | null;
  replyTo: CommunityMessage | null;
  // Typing
  typingUsers: string[];
  // Unread: communityId → unread count
  unreadMap: Record<string, number>;
  // Members (group info drawer)
  members: CommunityMember[];
  loadingMembers: boolean;
  membersNextCursor: string | null;
  communityInvites: CommunityInvite[];
  loadingInvites: boolean;
  // Socket status
  isSocketConnected: boolean;

  // Actions
  selectCommunity: (c: Community) => Promise<void>;
  clearCommunitySelection: () => void;
  sendMessage: (content: string) => Promise<void>;
  sendMediaMessage: (
    file: File,
    opts?: { message_type?: CommunityMediaMessageType; caption?: string }
  ) => Promise<void>;
  sendDocumentMessage: (file: File, opts?: { caption?: string }) => Promise<void>;
  sendVoiceMessage: (blob: Blob, durationMs: number) => Promise<void>;
  sendLocationMessage: (latitude: number, longitude: number, label: string) => Promise<void>;
  deleteMessages: (messageIds: string[]) => Promise<void>;
  forwardMessages: (messageIds: string[], targetCommunityId: string) => Promise<void>;
  pinMessage: (messageId: string) => Promise<void>;
  setReplyTo: (message: CommunityMessage | null) => void;
  loadMoreMessages: () => Promise<void>;
  refreshCommunities: () => Promise<void>;
  refreshDiscover: (search?: string) => Promise<void>;
  joinPublicCommunity: (communityId: string) => Promise<Community>;
  redeemInviteAndJoin: (token: string, opts?: { skipSelect?: boolean }) => Promise<Community>;
  createCommunityAndGroup: (
    name: string,
    description: string | undefined,
    visibility: 'public' | 'private' | 'invite_only'
  ) => Promise<Community>;
  leaveCommunity: (communityId: string) => Promise<void>;
  transferOwnership: (communityId: string, newOwnerClientId: string) => Promise<void>;
  deleteCommunity: (communityId: string) => Promise<void>;
  loadMembers: (communityId: string, append?: boolean) => Promise<void>;
  removeMember: (communityId: string, clientId: string) => Promise<void>;
  updateMemberRole: (
    communityId: string,
    clientId: string,
    role: 'owner' | 'moderator' | 'member'
  ) => Promise<void>;
  updateCommunityDetails: (
    communityId: string,
    patch: Parameters<typeof apiUpdateCommunity>[1]
  ) => Promise<Community>;
  uploadCommunityAvatar: (communityId: string, file: File) => Promise<Community>;
  refreshSelectedCommunity: () => Promise<void>;
  loadInvites: (communityId: string) => Promise<CommunityInvite[]>;
  ensureActiveClientInvite: (communityId: string) => Promise<CommunityInvite[]>;
  deleteInviteLinks: (communityId: string, tokens: string[]) => Promise<void>;
  emitTypingEvent: () => void;
  emitStopTypingEvent: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CommunityContext = createContext<CommunityContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const CommunityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, client } = useAuth();

  const [communities, setCommunities] = useState<Community[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [communitiesLoadError, setCommunitiesLoadError] = useState<string | null>(null);
  const [discoverCommunities, setDiscoverCommunities] = useState<Community[]>([]);
  const [loadingDiscover, setLoadingDiscover] = useState(false);
  const [discoverTotal, setDiscoverTotal] = useState(0);
  const [joiningCommunityId, setJoiningCommunityId] = useState<string | null>(null);
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
  const [activeGroup, setActiveGroup] = useState<CommunityGroup | null>(null);
  const [messages, setMessages] = useState<OptimisticMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [communityOpenError, setCommunityOpenError] = useState<string | null>(null);
  const [failedCommunityId, setFailedCommunityId] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [pinnedMessage, setPinnedMessage] = useState<CommunityMessage | null>(null);
  const [replyTo, setReplyTo] = useState<CommunityMessage | null>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({});
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersNextCursor, setMembersNextCursor] = useState<string | null>(null);
  const [communityInvites, setCommunityInvites] = useState<CommunityInvite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  // Refs to avoid stale closures in socket handlers
  const activeGroupRef = useRef<CommunityGroup | null>(null);
  const selectedCommunityRef = useRef<Community | null>(null);
  const typingTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const typingThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectInFlightRef = useRef<string | null>(null);

  activeGroupRef.current = activeGroup;
  selectedCommunityRef.current = selectedCommunity;

  // ---------------------------------------------------------------------------
  // Community list
  // ---------------------------------------------------------------------------

  const refreshCommunities = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingCommunities(true);
      setCommunitiesLoadError(null);
      const { communities: list } = await listMyCommunities();
      setCommunities(list);

      setUnreadMap((prev) => {
        const next = { ...prev };
        for (const c of list) {
          if (typeof c.unread_count === 'number') {
            next[c.community_id] = Math.max(prev[c.community_id] ?? 0, c.unread_count);
          }
        }
        return next;
      });
    } catch (err) {
      console.error('[CommunityCtx] Failed to load communities:', err);
      const message =
        err instanceof Error && err.message === 'Network Error'
          ? 'Could not reach the server. Check your connection and that the API is running.'
          : 'Could not load your communities. Please try again.';
      setCommunitiesLoadError(message);
    } finally {
      setLoadingCommunities(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshCommunities();
    } else {
      setCommunities([]);
      setCommunitiesLoadError(null);
      setDiscoverCommunities([]);
      setDiscoverTotal(0);
      setSelectedCommunity(null);
      setActiveGroup(null);
      setMessages([]);
      setUnreadMap({});
      setMembers([]);
      setMembersNextCursor(null);
      setCommunityInvites([]);
    }
  }, [isAuthenticated, refreshCommunities]);

  // ---------------------------------------------------------------------------
  // Select community → resolve default group → load messages
  // ---------------------------------------------------------------------------

  const clearCommunitySelection = useCallback(() => {
    if (activeGroupRef.current) {
      leaveGroupRoom(activeGroupRef.current.group_id);
    }
    selectInFlightRef.current = null;
    setSelectedCommunity(null);
    setActiveGroup(null);
    setMessages([]);
    setPinnedMessage(null);
    setReplyTo(null);
    setMembers([]);
    setMembersNextCursor(null);
    setCommunityInvites([]);
    setHasMoreMessages(false);
    setTypingUsers([]);
    setLoadingMessages(false);
    setCommunityOpenError(null);
    setFailedCommunityId(null);
  }, []);

  const selectCommunity = useCallback(
    async (c: Community) => {
      if (selectInFlightRef.current === c.community_id) return;
      selectInFlightRef.current = c.community_id;

      // Leave previous group room
      if (activeGroupRef.current) {
        leaveGroupRoom(activeGroupRef.current.group_id);
      }

      setCommunityOpenError(null);
      setFailedCommunityId(null);
      setSelectedCommunity(c);
      setActiveGroup(null);
      setMessages([]);
      setPinnedMessage(null);
      setReplyTo(null);
      setMembers([]);
      setMembersNextCursor(null);
      setCommunityInvites([]);
      setHasMoreMessages(false);
      setTypingUsers([]);
      setLoadingMessages(true);

      try {
        const defaultGroup = await resolveDefaultGroup(c.community_id);

        if (!defaultGroup) {
          throw new Error('No chat group available for this community');
        }

        setActiveGroup(defaultGroup);
        joinGroupRoom(defaultGroup.group_id);

        // Load initial messages
        const { messages: msgs, next_cursor, pinned_message } = await listMessages(
          c.community_id,
          defaultGroup.group_id
        );
        setMessages(msgs);
        setPinnedMessage(pinned_message ?? null);
        setHasMoreMessages(next_cursor !== null);

        // Mark all read
        if (msgs.length && defaultGroup.last_message_seq) {
          markRead(c.community_id, defaultGroup.group_id, defaultGroup.last_message_seq).catch(
            () => {}
          );
        }

        // Clear unread badge for this community
        setUnreadMap((prev) => ({ ...prev, [c.community_id]: 0 }));

        // Load members in background for sender role labels in chat
        listCommunityMembers(c.community_id, { limit: 100 })
          .then(({ members: list }) => setMembers(list))
          .catch(() => {});
      } catch (err) {
        const axiosMsg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : '';
        const message =
          axiosMsg === 'Network Error'
            ? 'Could not reach the server. Check your connection and that the API is running.'
            : err instanceof Error
              ? err.message
              : 'Could not open this community';
        console.error('[CommunityCtx] selectCommunity error:', err);
        setCommunityOpenError(message);
        setFailedCommunityId(c.community_id);
        setActiveGroup(null);
        setMessages([]);
        throw err;
      } finally {
        if (selectInFlightRef.current === c.community_id) {
          selectInFlightRef.current = null;
        }
        setLoadingMessages(false);
      }
    },
    []
  );

  const refreshDiscover = useCallback(async (search?: string) => {
    if (!isAuthenticated) return;
    try {
      setLoadingDiscover(true);
      const { communities: list, total } = await discoverPublicCommunities(
        search?.trim() ? { search: search.trim() } : {}
      );
      setDiscoverCommunities(list);
      setDiscoverTotal(total);
    } catch (err) {
      console.error('[CommunityCtx] Failed to discover communities:', err);
    } finally {
      setLoadingDiscover(false);
    }
  }, [isAuthenticated]);

  const joinPublicCommunity = useCallback(
    async (communityId: string): Promise<Community> => {
      setJoiningCommunityId(communityId);
      try {
        const { community, membership } = await apiJoinPublicCommunity(communityId);
        const withRole: Community = { ...community, my_role: membership.role, is_member: true };

        setCommunities((prev) => upsertCommunityInList(prev, withRole));
        setDiscoverCommunities((prev) =>
          prev.map((c) =>
            c.community_id === communityId ? { ...c, is_member: true, my_role: membership.role } : c
          )
        );

        await selectCommunity(withRole);

        void refreshCommunities();
        void refreshDiscover();
        return withRole;
      } finally {
        setJoiningCommunityId(null);
      }
    },
    [refreshCommunities, refreshDiscover, selectCommunity]
  );

  const redeemInviteAndJoin = useCallback(
    async (token: string, opts?: { skipSelect?: boolean }): Promise<Community> => {
      const { community_id } = await redeemInvite(token);
      const fetched = await getCommunity(community_id);
      const withRole: Community = {
        ...fetched,
        my_role: fetched.my_role ?? 'member',
        is_member: true,
      };

      setCommunities((prev) => upsertCommunityInList(prev, withRole));
      setDiscoverCommunities((prev) =>
        prev.map((c) =>
          c.community_id === community_id ? { ...c, is_member: true, my_role: withRole.my_role } : c
        )
      );

      if (!opts?.skipSelect) {
        await selectCommunity(withRole);
      }

      void refreshCommunities();
      void refreshDiscover();
      return withRole;
    },
    [refreshCommunities, refreshDiscover, selectCommunity]
  );

  // ---------------------------------------------------------------------------
  // Pagination (scroll up)
  // ---------------------------------------------------------------------------

  const loadMoreMessages = useCallback(async () => {
    const community = selectedCommunityRef.current;
    const group = activeGroupRef.current;
    if (!community || !group || !hasMoreMessages || loadingMessages) return;

    const oldest = messages[0];
    if (!oldest) return;

    setLoadingMessages(true);
    try {
      const { messages: older, next_cursor } = await listMessages(
        community.community_id,
        group.group_id,
        { before_seq: oldest.seq }
      );
      setMessages((prev) => [...older, ...prev]);
      setHasMoreMessages(next_cursor !== null);
    } catch (err) {
      console.error('[CommunityCtx] loadMoreMessages error:', err);
    } finally {
      setLoadingMessages(false);
    }
  }, [hasMoreMessages, loadingMessages, messages]);

  // ---------------------------------------------------------------------------
  // Send message with optimistic update
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback(
    async (content: string) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group || !content.trim()) return;

      const replyId = replyTo?.community_message_id;
      const optimisticId = `opt-${Date.now()}`;
      const optimistic: OptimisticMessage = {
        community_message_id: optimisticId,
        community_id: community.community_id,
        group_id: group.group_id,
        seq: -1,
        sender_client_id: client?.client_id,
        sender_name_cached: client?.name ?? 'You',
        message_type: 'text',
        content,
        reply_to_message_id: replyId,
        reply_preview: replyTo
          ? {
              community_message_id: replyTo.community_message_id,
              sender_name: replyTo.sender_name_cached,
              content_snippet: replyTo.content?.substring(0, 140),
              message_type: replyTo.message_type,
            }
          : undefined,
        deleted: false,
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };

      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);

      try {
        const sent = await apiSendMessage(community.community_id, group.group_id, content, {
          ...(replyId ? { reply_to_message_id: replyId } : {}),
        });
        setMessages((prev) => {
          const withoutDupes = prev.filter(
            (m) =>
              m.community_message_id !== optimisticId &&
              m.community_message_id !== sent.community_message_id
          );
          return [...withoutDupes, { ...sent, deleted: false }];
        });
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === optimisticId
              ? { ...m, _failed: true, _error: getCommunitySendFailureMessage(err) }
              : m
          )
        );
        console.error('[CommunityCtx] sendMessage error:', err);
        throw err;
      }
    },
    [client, replyTo]
  );

  const sendMediaMessage = useCallback(
    async (file: File, opts?: { message_type?: CommunityMediaMessageType; caption?: string }) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group) return;

      validateMediaFile(file);
      const messageType = opts?.message_type ?? inferMediaMessageType(file);
      const replyId = replyTo?.community_message_id;
      const optimisticId = `opt-${Date.now()}`;
      const previewUrl = URL.createObjectURL(file);

      const optimistic: OptimisticMessage = {
        community_message_id: optimisticId,
        community_id: community.community_id,
        group_id: group.group_id,
        seq: -1,
        sender_client_id: client?.client_id,
        sender_name_cached: client?.name ?? 'You',
        message_type: messageType,
        content: opts?.caption,
        media: {
          blob_url: previewUrl,
          filename: file.name,
          mime: file.type,
          size_bytes: file.size,
        },
        reply_to_message_id: replyId,
        reply_preview: replyTo
          ? {
              community_message_id: replyTo.community_message_id,
              sender_name: replyTo.sender_name_cached,
              content_snippet: replyTo.content?.substring(0, 140),
              message_type: replyTo.message_type,
            }
          : undefined,
        deleted: false,
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };

      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);

      try {
        const sent = await uploadAndSendMediaMessage(community.community_id, group.group_id, file, {
          message_type: messageType,
          filename: file.name,
          mime_type: file.type || 'application/octet-stream',
          content: opts?.caption,
          reply_to_message_id: replyId,
        });
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) => {
          const withoutDupes = prev.filter(
            (m) =>
              m.community_message_id !== optimisticId &&
              m.community_message_id !== sent.community_message_id
          );
          return [...withoutDupes, { ...sent, deleted: false }];
        });
      } catch (err) {
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === optimisticId
              ? { ...m, _failed: true, _error: getCommunitySendFailureMessage(err) }
              : m
          )
        );
        console.error('[CommunityCtx] sendMediaMessage error:', err);
        throw err;
      }
    },
    [client, replyTo]
  );

  const sendDocumentMessage = useCallback(
    async (file: File, opts?: { caption?: string }) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group) return;

      validateMediaFile(file, { asDocument: true });
      const replyId = replyTo?.community_message_id;
      const optimisticId = `opt-${Date.now()}`;
      const previewUrl = URL.createObjectURL(file);

      let pageCount: number | undefined;
      let thumbBlob: Blob | undefined;
      if (getDocumentKind(file.name, file.type) === 'pdf') {
        const extracted = await extractPdfPreview(file);
        if (extracted) {
          pageCount = extracted.pageCount;
          thumbBlob = extracted.thumbBlob;
        }
      }

      const optimistic: OptimisticMessage = {
        community_message_id: optimisticId,
        community_id: community.community_id,
        group_id: group.group_id,
        seq: -1,
        sender_client_id: client?.client_id,
        sender_name_cached: client?.name ?? 'You',
        message_type: 'document',
        content: opts?.caption,
        media: {
          blob_url: previewUrl,
          filename: file.name,
          mime: file.type,
          size_bytes: file.size,
          page_count: pageCount,
        },
        reply_to_message_id: replyId,
        deleted: false,
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };

      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);

      try {
        const sent = await uploadAndSendDocumentMessage(community.community_id, group.group_id, file, {
          content: opts?.caption,
          reply_to_message_id: replyId,
          thumbBlob,
          pageCount,
        });
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) => {
          const withoutDupes = prev.filter(
            (m) =>
              m.community_message_id !== optimisticId &&
              m.community_message_id !== sent.community_message_id
          );
          return [...withoutDupes, { ...sent, deleted: false }];
        });
      } catch (err) {
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === optimisticId
              ? { ...m, _failed: true, _error: getCommunitySendFailureMessage(err) }
              : m
          )
        );
        console.error('[CommunityCtx] sendDocumentMessage error:', err);
        throw err;
      }
    },
    [client, replyTo]
  );

  const sendVoiceMessage = useCallback(
    async (blob: Blob, durationMs: number) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group) return;

      const filename = voiceFilenameForBlob(blob);
      const mimeType = voiceMimeForBlob(blob);
      const file = new File([blob], filename, { type: mimeType });
      const replyId = replyTo?.community_message_id;
      const optimisticId = `opt-${Date.now()}`;
      const previewUrl = URL.createObjectURL(blob);

      const optimistic: OptimisticMessage = {
        community_message_id: optimisticId,
        community_id: community.community_id,
        group_id: group.group_id,
        seq: -1,
        sender_client_id: client?.client_id,
        sender_name_cached: client?.name ?? 'You',
        message_type: 'audio',
        media: {
          blob_url: previewUrl,
          filename,
          mime: mimeType,
          size_bytes: blob.size,
          duration_ms: durationMs,
        },
        reply_to_message_id: replyId,
        deleted: false,
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };

      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);

      try {
        const sent = await uploadAndSendMediaMessage(community.community_id, group.group_id, file, {
          message_type: 'audio',
          filename,
          mime_type: mimeType,
          duration_ms: durationMs,
          reply_to_message_id: replyId,
        });
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) => {
          const withoutDupes = prev.filter(
            (m) =>
              m.community_message_id !== optimisticId &&
              m.community_message_id !== sent.community_message_id
          );
          return [...withoutDupes, { ...sent, deleted: false }];
        });
      } catch (err) {
        URL.revokeObjectURL(previewUrl);
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === optimisticId
              ? { ...m, _failed: true, _error: getCommunitySendFailureMessage(err) }
              : m
          )
        );
        console.error('[CommunityCtx] sendVoiceMessage error:', err);
        throw err;
      }
    },
    [client, replyTo]
  );

  const sendLocationMessage = useCallback(
    async (latitude: number, longitude: number, label: string) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group) return;

      const replyId = replyTo?.community_message_id;
      const optimisticId = `opt-${Date.now()}`;

      const optimistic: OptimisticMessage = {
        community_message_id: optimisticId,
        community_id: community.community_id,
        group_id: group.group_id,
        seq: -1,
        sender_client_id: client?.client_id,
        sender_name_cached: client?.name ?? 'You',
        message_type: 'location',
        content: label,
        media: { latitude, longitude, location_label: label },
        reply_to_message_id: replyId,
        deleted: false,
        timestamp: new Date().toISOString(),
        _optimistic: true,
      };

      setMessages((prev) => [...prev, optimistic]);
      setReplyTo(null);

      try {
        const sent = await apiSendLocationMessage(community.community_id, group.group_id, {
          label,
          latitude,
          longitude,
          reply_to_message_id: replyId,
        });
        setMessages((prev) => {
          const withoutDupes = prev.filter(
            (m) =>
              m.community_message_id !== optimisticId &&
              m.community_message_id !== sent.community_message_id
          );
          return [...withoutDupes, { ...sent, deleted: false }];
        });
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === optimisticId
              ? { ...m, _failed: true, _error: getCommunitySendFailureMessage(err) }
              : m
          )
        );
        console.error('[CommunityCtx] sendLocationMessage error:', err);
        throw err;
      }
    },
    [client, replyTo]
  );

  const deleteMessages = useCallback(async (messageIds: string[]) => {
    const community = selectedCommunityRef.current;
    const group = activeGroupRef.current;
    if (!community || !group || !messageIds.length) return;

    const previous = messages;
    setMessages((prev) =>
      prev.map((m) =>
        messageIds.includes(m.community_message_id)
          ? { ...m, message_type: 'deleted', content: undefined, media: undefined, deleted: true }
          : m
      )
    );

    try {
      await Promise.all(
        messageIds.map((id) =>
          apiDeleteMessage(community.community_id, group.group_id, id)
        )
      );
      setPinnedMessage((prev) =>
        prev && messageIds.includes(prev.community_message_id) ? null : prev
      );
    } catch (err) {
      setMessages(previous);
      console.error('[CommunityCtx] deleteMessages error:', err);
      throw err;
    }
  }, [messages]);

  const forwardMessages = useCallback(
    async (messageIds: string[], targetCommunityId: string) => {
      const community = selectedCommunityRef.current;
      const group = activeGroupRef.current;
      if (!community || !group || !messageIds.length) return;

      await apiForwardMessages(community.community_id, group.group_id, {
        target_community_id: targetCommunityId,
        message_ids: messageIds,
      });
    },
    []
  );

  const pinMessage = useCallback(async (messageId: string) => {
    const community = selectedCommunityRef.current;
    const group = activeGroupRef.current;
    if (!community || !group) return;

    const { message, is_pinned } = await apiPinMessage(
      community.community_id,
      group.group_id,
      messageId
    );

    setMessages((prev) =>
      prev.map((m) => ({
        ...m,
        is_pinned: m.community_message_id === message.community_message_id ? is_pinned : false,
      }))
    );
    setPinnedMessage(is_pinned ? { ...message, deleted: false } : null);
  }, []);

  // ---------------------------------------------------------------------------
  // Create community + default "General" group (two-step)
  // ---------------------------------------------------------------------------

  const createCommunityAndGroup = useCallback(
    async (
      name: string,
      description: string | undefined,
      visibility: 'public' | 'private' | 'invite_only'
    ): Promise<Community> => {
      const community = await createCommunity({ name, description, visibility });

      // Create the default chat group — creator is owner so is_default is allowed
      try {
        await createGroup(community.community_id, {
          name: 'General',
          is_default: true,
          type: 'chat',
        });
      } catch (err) {
        console.warn('[CommunityCtx] Default group creation failed (community exists):', err);
      }

      await refreshCommunities();
      await refreshDiscover();
      const withRole: Community = { ...community, my_role: 'owner', is_member: true };
      await selectCommunity(withRole);
      return withRole;
    },
    [refreshCommunities, refreshDiscover, selectCommunity]
  );

  // ---------------------------------------------------------------------------
  // Leave community
  // ---------------------------------------------------------------------------

  const loadMembers = useCallback(async (communityId: string, append = false) => {
    try {
      setLoadingMembers(true);
      const cursor = append ? membersNextCursor : undefined;
      const { members: list, next_cursor } = await listCommunityMembers(communityId, {
        ...(cursor ? { cursor } : {}),
      });
      setMembers((prev) => (append ? [...prev, ...list] : list));
      setMembersNextCursor(next_cursor);
    } catch (err) {
      console.error('[CommunityCtx] loadMembers error:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [membersNextCursor]);

  const removeMember = useCallback(
    async (communityId: string, clientId: string) => {
      await apiRemoveCommunityMember(communityId, clientId);
      setMembers((prev) => prev.filter((m) => m.client_id !== clientId));
      setCommunities((prev) =>
        prev.map((c) =>
          c.community_id === communityId
            ? { ...c, member_count: Math.max(0, c.member_count - 1) }
            : c
        )
      );
      setSelectedCommunity((prev) =>
        prev?.community_id === communityId
          ? { ...prev, member_count: Math.max(0, prev.member_count - 1) }
          : prev
      );
    },
    []
  );

  const updateMemberRole = useCallback(
    async (
      communityId: string,
      clientId: string,
      role: 'owner' | 'moderator' | 'member'
    ) => {
      await setCommunityMemberRole(communityId, clientId, role);
      setMembers((prev) =>
        prev.map((m) => (m.client_id === clientId ? { ...m, role } : m))
      );
    },
    []
  );

  const updateCommunityDetails = useCallback(
    async (communityId: string, patch: Parameters<typeof apiUpdateCommunity>[1]) => {
      const updated = await apiUpdateCommunity(communityId, patch);
      setCommunities((prev) =>
        prev.map((c) => (c.community_id === communityId ? { ...c, ...updated } : c))
      );
      setSelectedCommunity((prev) =>
        prev?.community_id === communityId ? { ...prev, ...updated } : prev
      );
      return updated;
    },
    []
  );

  const applyCommunityUpdate = useCallback((updated: Community) => {
    setCommunities((prev) =>
      prev.map((c) => (c.community_id === updated.community_id ? { ...c, ...updated } : c))
    );
    setSelectedCommunity((prev) =>
      prev?.community_id === updated.community_id ? { ...prev, ...updated } : prev
    );
  }, []);

  const uploadCommunityAvatar = useCallback(
    async (communityId: string, file: File) => {
      const updated = await apiUploadCommunityAvatar(communityId, file);
      applyCommunityUpdate(updated);
      return updated;
    },
    [applyCommunityUpdate]
  );

  const refreshSelectedCommunity = useCallback(async () => {
    const current = selectedCommunityRef.current;
    if (!current) return;
    try {
      const refreshed = await getCommunity(current.community_id);
      setSelectedCommunity((prev) => (prev ? { ...prev, ...refreshed } : refreshed));
      setCommunities((prev) =>
        prev.map((c) =>
          c.community_id === refreshed.community_id ? { ...c, ...refreshed } : c
        )
      );
    } catch (err) {
      console.error('[CommunityCtx] refreshSelectedCommunity error:', err);
    }
  }, []);

  const loadInvites = useCallback(async (communityId: string) => {
    try {
      setLoadingInvites(true);
      const invites = await fetchListedInvites(communityId);
      setCommunityInvites(invites);
      return invites;
    } catch (err) {
      console.error('[CommunityCtx] loadInvites error:', err);
      return [];
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const ensureActiveClientInvite = useCallback(async (communityId: string) => {
    try {
      setLoadingInvites(true);
      const invites = await loadInvitesWithActiveClientInvite(communityId);
      setCommunityInvites(invites);
      return invites;
    } catch (err) {
      console.error('[CommunityCtx] ensureActiveClientInvite error:', err);
      return [];
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const deleteInviteLinks = useCallback(async (communityId: string, tokens: string[]) => {
    const uniqueTokens = [...new Set(tokens)];
    if (!uniqueTokens.length) return;

    const tokenSet = new Set(uniqueTokens);
    setCommunityInvites((prev) => prev.filter((i) => !tokenSet.has(i.token)));

    try {
      setLoadingInvites(true);
      ensureActiveInviteInflight.delete(communityId);
      await Promise.all(
        uniqueTokens.map((token) => deleteCommunityInvite(communityId, token))
      );
      const invites = await reloadInvitesAfterDelete(communityId);
      setCommunityInvites(invites);
    } catch (err) {
      console.error('[CommunityCtx] deleteInviteLinks refresh error:', err);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const loadInvitesRef = useRef(loadInvites);
  loadInvitesRef.current = loadInvites;

  const leaveCommunity = useCallback(
    async (communityId: string) => {
      await apiLeaveCommunity(communityId);
      if (selectedCommunityRef.current?.community_id === communityId) {
        if (activeGroupRef.current) leaveGroupRoom(activeGroupRef.current.group_id);
        setSelectedCommunity(null);
        setActiveGroup(null);
        setMessages([]);
        setPinnedMessage(null);
        setReplyTo(null);
      }
      await refreshCommunities();
      await refreshDiscover();
    },
    [refreshCommunities, refreshDiscover]
  );

  const clearCommunityFromState = useCallback(
    (communityId: string) => {
      setCommunities((prev) => prev.filter((c) => c.community_id !== communityId));
      if (selectedCommunityRef.current?.community_id === communityId) {
        if (activeGroupRef.current) leaveGroupRoom(activeGroupRef.current.group_id);
        setSelectedCommunity(null);
        setActiveGroup(null);
        setMessages([]);
        setPinnedMessage(null);
        setReplyTo(null);
        setMembers([]);
      }
    },
    []
  );

  const transferOwnership = useCallback(
    async (communityId: string, newOwnerClientId: string) => {
      await apiTransferOwnership(communityId, newOwnerClientId);
      setSelectedCommunity((prev) =>
        prev?.community_id === communityId ? { ...prev, my_role: 'member' } : prev
      );
      setCommunities((prev) =>
        prev.map((c) =>
          c.community_id === communityId ? { ...c, my_role: 'member' } : c
        )
      );
      setMembers((prev) =>
        prev.map((m) => {
          if (m.client_id === newOwnerClientId) return { ...m, role: 'owner' };
          if (m.client_id === client?.client_id) return { ...m, role: 'member' };
          return m;
        })
      );
    },
    [client]
  );

  const deleteCommunity = useCallback(
    async (communityId: string) => {
      await apiDeleteCommunity(communityId);
      clearCommunityFromState(communityId);
      await refreshCommunities();
      await refreshDiscover();
    },
    [clearCommunityFromState, refreshCommunities, refreshDiscover]
  );

  // ---------------------------------------------------------------------------
  // Typing indicators
  // ---------------------------------------------------------------------------

  const emitTypingEvent = useCallback(() => {
    const group = activeGroupRef.current;
    if (!group || !client) return;
    // Throttle to once per 2 s
    if (typingThrottleRef.current) return;
    emitTyping(group.group_id, client.client_id, client.name);
    typingThrottleRef.current = setTimeout(() => {
      typingThrottleRef.current = null;
    }, 2000);
  }, [client]);

  const emitStopTypingEvent = useCallback(() => {
    const group = activeGroupRef.current;
    if (!group || !client) return;
    if (typingThrottleRef.current) {
      clearTimeout(typingThrottleRef.current);
      typingThrottleRef.current = null;
    }
    emitStopTyping(group.group_id, client.client_id);
  }, [client]);

  // ---------------------------------------------------------------------------
  // Socket event listeners
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated) return;

    let socketCleanup: (() => void) | undefined;

    const checkSocket = () => {
      const socket = chatService.getSocket();
      if (!socket) return false;

      setIsSocketConnected(socket.connected);

      const onConnect = () => setIsSocketConnected(true);
      const onDisconnect = () => setIsSocketConnected(false);

      const onMessageNew = (data: {
        group_id: string;
        community_id: string;
        message: CommunityMessage;
      }) => {
        const currentGroup = activeGroupRef.current;
        const currentCommunity = selectedCommunityRef.current;

        if (currentGroup && data.group_id === currentGroup.group_id) {
          // Active conversation — append and mark read
          setMessages((prev) => {
            const confirmed = { ...data.message, deleted: !!data.message.deleted_at };

            // Already have this message (e.g. API response landed first)
            if (
              prev.some((m) => m.community_message_id === confirmed.community_message_id)
            ) {
              return prev;
            }

            // Own message via socket: swap optimistic placeholder instead of appending
            if (confirmed.sender_client_id === client?.client_id) {
              const optimisticIdx = prev.findIndex(
                (m) =>
                  m._optimistic &&
                  m.group_id === data.group_id &&
                  m.sender_client_id === client?.client_id
              );
              if (optimisticIdx >= 0) {
                const next = [...prev];
                next[optimisticIdx] = confirmed;
                return next;
              }
            }

            return [...prev, confirmed];
          });
          // Mark read silently
          if (currentCommunity && data.message.seq) {
            markRead(currentCommunity.community_id, data.group_id, data.message.seq).catch(() => {});
          }
        } else {
          // Background community — increment unread badge
          // Find communityId for this group from communities list
          setCommunities((prev) => {
            // Update last_message_preview on the community's sidebar entry if we can
            return prev;
          });
          // We track unread by communityId; use community_id from the event
          setUnreadMap((prev) => ({
            ...prev,
            [data.community_id]: (prev[data.community_id] ?? 0) + 1,
          }));
        }
      };

      const onMessageUpdated = (data: { group_id: string; message: CommunityMessage }) => {
        if (activeGroupRef.current?.group_id !== data.group_id) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === data.message.community_message_id
              ? { ...data.message, deleted: false }
              : m
          )
        );
      };

      const onMessageDeleted = (data: {
        group_id: string;
        community_message_id: string;
      }) => {
        if (activeGroupRef.current?.group_id !== data.group_id) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.community_message_id === data.community_message_id
              ? { ...m, message_type: 'deleted', content: undefined, media: undefined, deleted: true, is_pinned: false }
              : m
          )
        );
        setPinnedMessage((prev) =>
          prev?.community_message_id === data.community_message_id ? null : prev
        );
      };

      const onMessagePinned = (data: {
        group_id: string;
        community_message_id: string;
        is_pinned: boolean;
        message?: CommunityMessage;
      }) => {
        if (activeGroupRef.current?.group_id !== data.group_id) return;
        setMessages((prev) =>
          prev.map((m) => ({
            ...m,
            is_pinned:
              data.is_pinned && m.community_message_id === data.community_message_id
                ? true
                : m.community_message_id === data.community_message_id
                  ? false
                  : data.is_pinned
                    ? false
                    : m.is_pinned,
          }))
        );
        if (data.is_pinned && data.message) {
          setPinnedMessage({ ...data.message, deleted: false });
        } else {
          setPinnedMessage(null);
        }
      };

      const onTyping = (data: {
        group_id: string;
        client_id: string;
        display_name?: string;
      }) => {
        if (activeGroupRef.current?.group_id !== data.group_id) return;
        if (data.client_id === client?.client_id) return; // don't show self
        const name = data.display_name || data.client_id;
        setTypingUsers((prev) => (prev.includes(name) ? prev : [...prev, name]));

        // Auto-clear after 3 s if no stopTyping arrives
        const existing = typingTimeouts.current.get(data.client_id);
        if (existing) clearTimeout(existing);
        typingTimeouts.current.set(
          data.client_id,
          setTimeout(() => {
            setTypingUsers((prev) => prev.filter((n) => n !== name));
            typingTimeouts.current.delete(data.client_id);
          }, 3000)
        );
      };

      const onStopTyping = (data: { group_id: string; client_id: string }) => {
        if (activeGroupRef.current?.group_id !== data.group_id) return;
        const timeout = typingTimeouts.current.get(data.client_id);
        if (timeout) {
          clearTimeout(timeout);
          typingTimeouts.current.delete(data.client_id);
        }
        setTypingUsers([]);
      };

      const onMemberBanned = (data: { community_id: string; client_id: string }) => {
        if (selectedCommunityRef.current?.community_id === data.community_id) {
          setMembers((prev) => prev.filter((m) => m.client_id !== data.client_id));
        }
        if (data.client_id === client?.client_id) {
          if (activeGroupRef.current) leaveGroupRoom(activeGroupRef.current.group_id);
          setSelectedCommunity(null);
          setActiveGroup(null);
          setMessages([]);
          setMembers([]);
          refreshCommunities();
          refreshDiscover();
        } else {
          setCommunities((prev) =>
            prev.map((c) =>
              c.community_id === data.community_id
                ? { ...c, member_count: Math.max(0, c.member_count - 1) }
                : c
            )
          );
          setSelectedCommunity((prev) =>
            prev?.community_id === data.community_id
              ? { ...prev, member_count: Math.max(0, prev.member_count - 1) }
              : prev
          );
        }
      };

      const onCommunityArchived = (data: { community_id: string }) => {
        setCommunities((prev) => prev.filter((c) => c.community_id !== data.community_id));
        if (selectedCommunityRef.current?.community_id === data.community_id) {
          if (activeGroupRef.current) leaveGroupRoom(activeGroupRef.current.group_id);
          setSelectedCommunity(null);
          setActiveGroup(null);
          setMessages([]);
          setPinnedMessage(null);
          setReplyTo(null);
          setMembers([]);
        }
      };

      const onOwnershipTransferred = (data: {
        community_id: string;
        previous_owner_client_id: string;
        new_owner_client_id: string;
      }) => {
        if (selectedCommunityRef.current?.community_id !== data.community_id) return;
        if (data.new_owner_client_id === client?.client_id) {
          setSelectedCommunity((prev) => (prev ? { ...prev, my_role: 'owner' } : prev));
        } else if (data.previous_owner_client_id === client?.client_id) {
          setSelectedCommunity((prev) => (prev ? { ...prev, my_role: 'member' } : prev));
        }
        setMembers((prev) =>
          prev.map((m) => {
            if (m.client_id === data.new_owner_client_id) return { ...m, role: 'owner' };
            if (m.client_id === data.previous_owner_client_id) return { ...m, role: 'member' };
            return m;
          })
        );
      };

      const onMemberLeft = (data: { community_id: string; client_id: string }) => {
        if (selectedCommunityRef.current?.community_id !== data.community_id) return;
        setMembers((prev) => prev.filter((m) => m.client_id !== data.client_id));
        setCommunities((prev) =>
          prev.map((c) =>
            c.community_id === data.community_id
              ? { ...c, member_count: Math.max(0, c.member_count - 1) }
              : c
          )
        );
        setSelectedCommunity((prev) =>
          prev?.community_id === data.community_id
            ? { ...prev, member_count: Math.max(0, prev.member_count - 1) }
            : prev
        );
      };

      const onInviteRedeemed = (data: {
        community_id: string;
        token: string;
        use_count: number;
        revoked_at?: string | null;
      }) => {
        if (selectedCommunityRef.current?.community_id !== data.community_id) return;
        setCommunityInvites((prev) => {
          const hasToken = prev.some((i) => i.token === data.token);
          if (!hasToken) {
            void loadInvitesRef.current(data.community_id);
            return prev;
          }
          return prev.map((i) =>
            i.token === data.token
              ? {
                  ...i,
                  use_count: data.use_count,
                  max_uses: i.max_uses ?? 1,
                  revoked_at: data.revoked_at ?? i.revoked_at ?? new Date().toISOString(),
                }
              : i
          );
        });
      };

      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('community:message:new', onMessageNew);
      socket.on('community:message:updated', onMessageUpdated);
      socket.on('community:message:deleted', onMessageDeleted);
      socket.on('community:message:pinned', onMessagePinned);
      socket.on('community:typing', onTyping);
      socket.on('community:stopTyping', onStopTyping);
      socket.on('community:member:banned', onMemberBanned);
      socket.on('community:archived', onCommunityArchived);
      socket.on('community:ownership:transferred', onOwnershipTransferred);
      socket.on('community:member:left', onMemberLeft);
      socket.on('community:invite:redeemed', onInviteRedeemed);

      socketCleanup = () => {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('community:message:new', onMessageNew);
        socket.off('community:message:updated', onMessageUpdated);
        socket.off('community:message:deleted', onMessageDeleted);
        socket.off('community:message:pinned', onMessagePinned);
        socket.off('community:typing', onTyping);
        socket.off('community:stopTyping', onStopTyping);
        socket.off('community:member:banned', onMemberBanned);
        socket.off('community:archived', onCommunityArchived);
        socket.off('community:ownership:transferred', onOwnershipTransferred);
        socket.off('community:member:left', onMemberLeft);
        socket.off('community:invite:redeemed', onInviteRedeemed);
      };
      return true;
    };

    // The socket may not be connected immediately after mount; retry shortly
    if (!checkSocket()) {
      const retryTimer = setTimeout(() => {
        checkSocket();
      }, 500);

      return () => {
        clearTimeout(retryTimer);
        socketCleanup?.();
        typingTimeouts.current.forEach((t) => clearTimeout(t));
        typingTimeouts.current.clear();
      };
    }

    return () => {
      socketCleanup?.();
      typingTimeouts.current.forEach((t) => clearTimeout(t));
      typingTimeouts.current.clear();
    };
  }, [isAuthenticated, client, refreshCommunities, refreshDiscover]);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------

  return (
    <CommunityContext.Provider
      value={{
        communities,
        loadingCommunities,
        communitiesLoadError,
        discoverCommunities,
        loadingDiscover,
        discoverTotal,
        joiningCommunityId,
        selectedCommunity,
        activeGroup,
        messages,
        loadingMessages,
        communityOpenError,
        failedCommunityId,
        hasMoreMessages,
        pinnedMessage,
        replyTo,
        typingUsers,
        unreadMap,
        members,
        loadingMembers,
        membersNextCursor,
        communityInvites,
        loadingInvites,
        isSocketConnected,
        selectCommunity,
        clearCommunitySelection,
        sendMessage,
        sendMediaMessage,
        sendDocumentMessage,
        sendVoiceMessage,
        sendLocationMessage,
        deleteMessages,
        forwardMessages,
        pinMessage,
        setReplyTo,
        loadMoreMessages,
        refreshCommunities,
        refreshDiscover,
        joinPublicCommunity,
        redeemInviteAndJoin,
        createCommunityAndGroup,
        leaveCommunity,
        transferOwnership,
        deleteCommunity,
        loadMembers,
        removeMember,
        updateMemberRole,
        updateCommunityDetails,
        uploadCommunityAvatar,
        refreshSelectedCommunity,
        loadInvites,
        ensureActiveClientInvite,
        deleteInviteLinks,
        emitTypingEvent,
        emitStopTypingEvent,
      }}
    >
      {children}
    </CommunityContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useCommunity = () => {
  const ctx = useContext(CommunityContext);
  if (!ctx) throw new Error('useCommunity must be used within a CommunityProvider');
  return ctx;
};
